import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useDatabaseContext } from './DatabaseContext';
import { AggregateRequest, AggregateResponse, AggregateSectionRequestName, AggregateSectionState } from '../types/inventory';
import { fetchInventoryAggregate, fetchArticlesByIds, fetchInventoryAggregateSection } from '../services/inventory.service';

export type BasketLine = {
  key: string;
  sku_id: string;
  supplier_id?: number;
  supplierLocationGroup?: string;
  label: string;
  targetMonths?: number;
  suggestedQty?: number;
  quantity?: number;
};

type InventoryContextState = {
  data: AggregateResponse | null;
  loading: boolean;
  error: string | null;
  sectionStates: Partial<Record<AggregateSectionRequestName, AggregateSectionState>>;
  ensureSections: (sectionNames: AggregateSectionRequestName[]) => Promise<void>;
  selectedArticleIds: string[];
  setSelectedArticleIds: (ids: string[]) => void;
  setArticlesFromSelector: (ids: string[]) => void;
  refresh: () => Promise<void>;
  basket: BasketLine[]; // legacy, will be deprecated
  addToBasket: (line: BasketLine) => void; // legacy
  removeFromBasket: (key: string) => void; // legacy
  clearBasket: () => void; // legacy
  updateBasketLine: (key: string, partial: Partial<BasketLine>) => void; // legacy
  supplierSelections: Array<{ supplier_id: number; supplier_name?: string }>;
  locationSelections: Array<{ location_group: string }>;
  addSupplierSelection: (supplier_id: number, supplier_name?: string) => void;
  removeSupplierSelection: (supplier_id: number) => void;
  addLocationSelection: (location_group: string) => void;
  removeLocationSelection: (location_group: string) => void;
  clearSelections: () => void;
  // Draft
  draft: any | null;
  setDraft: (d: any | null) => void;
  // Draft overlay
  applyDraftToProjections: boolean;
  setApplyDraftToProjections: (v: boolean) => void;
  // Global toggle to filter left content by current draft
  applyLeftFilter: boolean;
  setApplyLeftFilter: (v: boolean) => void;
  // Global negative stock handling
  allowNegativeStock: boolean;
  setAllowNegativeStock: (v: boolean) => void;
  // Global service level (probability, e.g., 0.95), persisted in cookies
  serviceLevel: number;
  setServiceLevel: (v: number) => void;
  // Global CB coverage months (e.g., 16), persisted in cookies
  coverageMonths: number;
  setCoverageMonths: (v: number) => void;
  effectiveProjectedInventory: any[];
  effectiveReorderMonthsSS: any[];
  effectiveReorderMonthsCB: any[];
  // Global planning mode (ss | cb)
  planningMode: 'ss' | 'cb';
  setPlanningMode: (m: 'ss' | 'cb') => void;
};

const InventoryPlanningContext = createContext<InventoryContextState | undefined>(undefined);

const isAggregateSnapshotModeEnabled = () =>
  process.env.REACT_APP_INVENTORY_AGGREGATE_SNAPSHOT_ENABLED === 'true';

const BACKGROUND_PREFETCH_SECTIONS: AggregateSectionRequestName[] = [
  'sales',
  'forecast',
  'server_projection',
];

export const InventoryPlanningProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { activeDatabase } = useDatabaseContext();
  const [data, setData] = useState<AggregateResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sectionStates, setSectionStates] = useState<Partial<Record<AggregateSectionRequestName, AggregateSectionState>>>({});
  const [selectedArticleIds, setSelectedArticleIds] = useState<string[]>([]);
  const [baseSelectedArticleIds, setBaseSelectedArticleIds] = useState<string[]>([]);
  const [basket, setBasket] = useState<BasketLine[]>([]);
  const [supplierSelections, setSupplierSelections] = useState<Array<{ supplier_id: number; supplier_name?: string }>>([]);
  const [locationSelections, setLocationSelections] = useState<Array<{ location_group: string }>>([]);
  const [draft, setDraft] = useState<any | null>(null);
  const [applyDraftToProjections, setApplyDraftToProjections] = useState(false);
  const [applyLeftFilter, setApplyLeftFilter] = useState(false);
  const [planningMode, setPlanningMode] = useState<'ss' | 'cb'>('ss');
  const [allowNegativeStock, setAllowNegativeStock] = useState<boolean>(true);
  const [serviceLevel, setServiceLevel] = useState<number>(() => {
    try {
      const match = document.cookie.match(/(?:^|; )service_level=([^;]+)/);
      const val = match ? decodeURIComponent(match[1]) : '';
      const num = parseFloat(val);
      return isFinite(num) && num > 0 && num <= 1 ? num : 0.95;
    } catch {
      return 0.95;
    }
  });
  const [coverageMonths, setCoverageMonths] = useState<number>(() => {
    try {
      const match = document.cookie.match(/(?:^|; )coverage_months=([^;]+)/);
      const val = match ? decodeURIComponent(match[1]) : '';
      const num = parseInt(val, 10);
      return Number.isFinite(num) && num >= 1 && num <= 36 ? num : 16;
    } catch {
      return 16;
    }
  });
  // Persist service level in cookie
  React.useEffect(() => {
    try {
      const days = 365;
      const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
      document.cookie = `service_level=${encodeURIComponent(String(serviceLevel))}; Expires=${expires}; Path=/`;
    } catch {}
  }, [serviceLevel]);
  React.useEffect(() => {
    try {
      const days = 365;
      const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
      document.cookie = `coverage_months=${encodeURIComponent(String(coverageMonths))}; Expires=${expires}; Path=/`;
    } catch {}
  }, [coverageMonths]);

  const latestRequestIdRef = useRef(0);
  const activeAggregateAbortRef = useRef<AbortController | null>(null);

  // Compute effective lead time by SKU, optionally constrained to selected suppliers/locations
  const effectiveLeadTimeBySku = useMemo(() => {
    const bySkuAll: Record<string, number> = {};
    const bySkuBySupplier: Record<string, Record<string, number>> = {};
    for (const r of ((data?.lead_time_months || []) as any[])) {
      const sku = String((r as any).sku_id);
      const sup = String((r as any).supplier_id ?? '');
      const L = Number((r as any).lead_time_months || 0);
      bySkuAll[sku] = Math.max(bySkuAll[sku] || 0, isFinite(L) ? L : 0);
      (bySkuBySupplier[sku] ||= {});
      if (sup) bySkuBySupplier[sku][sup] = Math.max(bySkuBySupplier[sku][sup] || 0, isFinite(L) ? L : 0);
    }
    const selectedSupIds = new Set<string>((supplierSelections || []).map((s) => String(s.supplier_id)));
    if ((locationSelections || []).length > 0) {
      const selectedLocs = new Set((locationSelections || []).map((l) => String(l.location_group)));
      for (const sup of ((data?.suppliers || []) as any[])) {
        const loc = String((sup as any)?.supplier_location_group || '');
        if (selectedLocs.has(loc)) selectedSupIds.add(String((sup as any).supplier_id));
      }
    }
    const useSelected = applyDraftToProjections && selectedSupIds.size > 0;
    const out: Record<string, number> = {};
    for (const sku of Object.keys(bySkuAll)) {
      if (useSelected) {
        const perSup = bySkuBySupplier[sku] || {};
        let maxForSel = 0;
        Array.from(selectedSupIds).forEach((sid) => {
          if (sid in perSup) maxForSel = Math.max(maxForSel, perSup[sid]);
        });
        out[sku] = maxForSel > 0 ? maxForSel : bySkuAll[sku];
      } else {
        out[sku] = bySkuAll[sku];
      }
    }
    return out;
  }, [data?.lead_time_months, data?.suppliers, applyDraftToProjections, supplierSelections, locationSelections]);

  // Build draft inbound by month using lead times (arrival at month L)
  const draftInboundByMonth = useMemo(() => {
    if (!applyDraftToProjections || !draft || !data) return {} as Record<string, Record<number, number>>;
    const inbound: Record<string, Record<number, number>> = {};
    for (const c of ((draft?.containers || []) as any[])) {
      for (const ln of ((c?.lines || []) as any[])) {
        const sku = String(ln?.article_ref || ln?.sku_id || '');
        if (!sku) continue;
        const qty = Number(ln?.quantity || 0);
        if (!(qty > 0)) continue;
        const L = Math.max(0, Number(effectiveLeadTimeBySku[sku] || 0));
        (inbound[sku] ||= {});
        inbound[sku][L] = (inbound[sku][L] || 0) + qty;
      }
    }
    return inbound;
  }, [applyDraftToProjections, draft, data, effectiveLeadTimeBySku]);

  // Compute unique article ids present in the current draft (from both lines and containers)
  const getDraftArticleIds = useCallback((): string[] => {
    try {
      const idsFromLines = ((draft?.lines || []) as any[]).map((l: any) => String(l.article_supplier_article_ref));
      const idsFromContainers = ((draft?.containers || []) as any[]).flatMap((c: any) =>
        ((c?.lines || []) as any[]).map((ln: any) => String(ln.article_ref))
      );
      const uniq = Array.from(new Set([...(idsFromLines || []), ...(idsFromContainers || [])]));
      // Stabilize order to avoid unnecessary state changes
      uniq.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
      return uniq;
    } catch {
      return [];
    }
  }, [draft]);

  // Keep left panel filter in sync when draft or toggle changes
  React.useEffect(() => {
    const draftIds = getDraftArticleIds();
    const baseIds = baseSelectedArticleIds;
    const useDraft = applyLeftFilter && draftIds.length > 0;
    const target = useDraft ? draftIds : baseIds;
    const sameLength = (target || []).length === (selectedArticleIds || []).length;
    const sameContent = sameLength && (target || []).every((id, idx) => String(id) === String(selectedArticleIds[idx]));
    if (!sameContent) {
      setSelectedArticleIds(target);
    }
  }, [applyLeftFilter, getDraftArticleIds, baseSelectedArticleIds, selectedArticleIds, setSelectedArticleIds]);

  // DEBUG helpers: focus logs on SKUs present in the current draft (limit to a handful)
  const debugSkuSet = useMemo(() => {
    try {
      const keys = Object.keys(draftInboundByMonth || {});
      return new Set(keys.slice(0, 10));
    } catch {
      return new Set<string>();
    }
  }, [draftInboundByMonth]);

  // Effective projected inventory = base + (backend inbound + draft inbound overlay)
  // Helper function: Compute historical average for a SKU using latest 12 months
  const computeHistoricalAverage = useCallback((skuId: string, lookbackMonths: number = 12): number => {
    if (!data) return 0;
    
    const skuStr = String(skuId);
    const forecastData: Map<string, number> = new Map(); // month -> qty
    const actualData: Map<string, number> = new Map(); // month -> qty
    
    // Process historical forecasts
    for (const f of (data.forecast_series || [])) {
      if (String(f.article) !== skuStr) continue;
      try {
        const date = new Date(f.date);
        if (isNaN(date.getTime())) continue;
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const qty = Number(f.sales || 0);
        // Keep latest prediction per month (if multiple predictions exist for same month)
        if (!forecastData.has(monthKey) || forecastData.get(monthKey)! < qty) {
          forecastData.set(monthKey, qty);
        }
      } catch (e) {
        // Skip invalid dates
        continue;
      }
    }
    
    // Process actuals
    for (const a of (data.sales_history || [])) {
      if (String(a.ref) !== skuStr) continue;
      try {
        const date = new Date(a.date);
        if (isNaN(date.getTime())) continue;
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const qty = Number(a.quantity || 0);
        actualData.set(monthKey, qty);
      } catch (e) {
        // Skip invalid dates
        continue;
      }
    }
    
    // Combine: prefer actuals over forecasts when both exist
    const combined = new Map<string, number>();
    for (const [month, qty] of Array.from(forecastData.entries())) {
      combined.set(month, qty);
    }
    for (const [month, qty] of Array.from(actualData.entries())) {
      combined.set(month, qty); // Overwrite forecasts with actuals
    }
    
    if (combined.size === 0) return 0;
    
    // Sort by month descending (YYYY-MM format sorts correctly) and take latest lookbackMonths
    const sortedMonths = Array.from(combined.keys()).sort().reverse().slice(0, lookbackMonths);
    if (sortedMonths.length === 0) return 0;
    
    const values = sortedMonths.map(m => combined.get(m)!).filter(v => !isNaN(v) && isFinite(v));
    if (values.length === 0) return 0;
    
    const avg = values.reduce((sum, v) => sum + v, 0) / values.length;
    return isFinite(avg) ? avg : 0;
  }, [data]);

  // Helper function: Get last prediction month index for a SKU
  const getLastPredictionMonthIndex = useCallback((skuId: string, asOfDate: string): number | null => {
    if (!data) return null;
    
    const skuStr = String(skuId);
    let lastMonthIndex: number | null = null;
    
    try {
      const asOf = new Date(asOfDate);
      if (isNaN(asOf.getTime())) return null;
      const asOfYear = asOf.getFullYear();
      const asOfMonth = asOf.getMonth();
      
      // Find last forecast month for this SKU
      for (const f of (data.forecast_series || [])) {
        if (String(f.article) !== skuStr) continue;
        try {
          const date = new Date(f.date);
          if (isNaN(date.getTime())) continue;
          
          // Calculate month index relative to asOfDate (0-based)
          const monthDiff = (date.getFullYear() - asOfYear) * 12 + (date.getMonth() - asOfMonth);
          
          // Only consider future months (monthDiff >= 0)
          if (monthDiff >= 0 && (lastMonthIndex === null || monthDiff > lastMonthIndex)) {
            lastMonthIndex = monthDiff;
          }
        } catch (e) {
          continue;
        }
      }
    } catch (e) {
      return null;
    }
    
    return lastMonthIndex;
  }, [data]);

  // Helper function: Normalize forecast values (replace placeholder zeros with avg_forecast)
  const normalizeForecastValues = useCallback((
    forecastValues: number[],
    lastPredictionIndex: number | null,
    avgForecast: number,
    skuId?: string,
    verbose: boolean = false
  ): number[] => {
    if (verbose) {
      console.log(`[normalizeForecastValues] SKU=${skuId || 'unknown'}: lastPredictionIndex=${lastPredictionIndex}, avgForecast=${avgForecast.toFixed(2)}`);
      console.log(`[normalizeForecastValues] Input forecasts (first 12):`, forecastValues.slice(0, 12));
    }
    
    // If last_prediction_index is null/negative, ALL zeros are placeholders (no predictions available)
    // Replace all zeros with avg_forecast
    if (lastPredictionIndex === null || lastPredictionIndex < 0) {
      const result = forecastValues.map(fv => {
        const val = Number(fv) || 0;
        return val > 0 ? val : (avgForecast > 0 ? avgForecast : 0);
      });
      if (verbose) {
        console.log(`[normalizeForecastValues] No valid lastPredictionIndex, replacing ALL zeros with avgForecast=${avgForecast.toFixed(2)}`);
      }
      return result;
    }
    
    const normalized: number[] = [];
    const lastIdx = lastPredictionIndex;
    
    for (let i = 0; i < forecastValues.length; i++) {
      const fv = Number(forecastValues[i]) || 0;
      
      if (i <= lastIdx) {
        // Real forecast (keep as-is, even if zero)
        normalized.push(fv);
        if (verbose && i < 12) {
          console.log(`[normalizeForecastValues] Month ${i}: real forecast = ${fv.toFixed(2)}`);
        }
      } else {
        // Placeholder - replace zero with avg_forecast
        if (fv <= 0) {
          const replacement = avgForecast > 0 ? avgForecast : 0;
          normalized.push(replacement);
          if (verbose && i < 12) {
            console.log(`[normalizeForecastValues] Month ${i}: placeholder zero -> ${replacement.toFixed(2)}`);
          }
        } else {
          // Shouldn't happen, but handle it
          normalized.push(fv);
          if (verbose && i < 12) {
            console.log(`[normalizeForecastValues] Month ${i}: unexpected non-zero placeholder = ${fv.toFixed(2)}`);
          }
        }
      }
    }
    
    if (verbose) {
      console.log(`[normalizeForecastValues] Output forecasts (first 12):`, normalized.slice(0, 12));
    }
    
    return normalized;
  }, []);

  // Helper function: Compute coverage from forecast consumption
  const computeCoverageFromForecastConsumption = useCallback((
    initialStock: number,
    forecastValues: number[],
    historicalAvg: number,
    lastPredictionIndex?: number | null,
    avgForecast?: number,
    skuId?: string,
    verbose: boolean = false
  ): number => {
    console.debug(
      `[coverage] SKU=${skuId ?? '?'} initial_stock=${initialStock.toFixed(2)} historical_avg=${historicalAvg.toFixed(2)} last_pred_idx=${lastPredictionIndex ?? '?'} avg_forecast=${avgForecast ?? '?'} forecasts_len=${forecastValues.length}`
    );
    if (verbose) {
      console.log(`[computeCoverage] SKU=${skuId || 'unknown'}: initialStock=${initialStock.toFixed(2)}, historicalAvg=${historicalAvg.toFixed(2)}, lastPredictionIndex=${lastPredictionIndex}, avgForecast=${avgForecast}`);
      console.log(`[computeCoverage] Input forecasts (first 12):`, forecastValues.slice(0, 12));
    }
    
    // Normalize forecasts if lastPredictionIndex is provided
    let normalizedForecasts = forecastValues;
    if (lastPredictionIndex !== undefined && avgForecast !== undefined) {
      normalizedForecasts = normalizeForecastValues(forecastValues, lastPredictionIndex, avgForecast, skuId, verbose);
      console.debug(
        `[coverage] SKU=${skuId ?? '?'} normalized forecasts (last_pred_idx=${lastPredictionIndex}), first 6:`,
        normalizedForecasts.slice(0, 6)
      );
      if (verbose) {
        console.log(`[computeCoverage] After normalization (first 12):`, normalizedForecasts.slice(0, 12));
      }
    }
    
    let remainingStock = initialStock;
    let coverage = 0;
    
    // Consume forecasts month-by-month
    for (let monthIdx = 0; monthIdx < normalizedForecasts.length; monthIdx++) {
      const forecast = normalizedForecasts[monthIdx];
      const forecastVal = Number(forecast) || 0;
      
      if (verbose && monthIdx < 20) {
        console.log(`[computeCoverage] Month ${monthIdx}: stock=${remainingStock.toFixed(2)}, forecast=${forecastVal.toFixed(2)}, coverage_before=${coverage.toFixed(2)}`);
      }
      
      // Real zero forecasts: consume 0 units, add 1.0 to coverage (month passes with no consumption)
      // Allow negative coverage: if stock is negative, coverage can go negative
      if (forecastVal <= 0) {
        coverage += 1.0;
        if (verbose && monthIdx < 20) {
          console.log(`[computeCoverage] Month ${monthIdx}: zero forecast -> coverage += 1.0, coverage_after=${coverage.toFixed(2)}, stock=${remainingStock.toFixed(2)}`);
        }
        continue;
      }
      
      if (remainingStock >= forecastVal) {
        // Full month can be supplied
        remainingStock -= forecastVal;
        coverage += 1.0;
        if (verbose && monthIdx < 20) {
          console.log(`[computeCoverage] Month ${monthIdx}: full month -> stock -= ${forecastVal.toFixed(2)}, coverage += 1.0, stock_after=${remainingStock.toFixed(2)}, coverage_after=${coverage.toFixed(2)}`);
        }
      } else if (remainingStock > 0) {
        // Partial month
        const partialCoverage = remainingStock / forecastVal;
        coverage += partialCoverage;
        if (verbose) {
          console.log(`[computeCoverage] Month ${monthIdx}: PARTIAL -> stock=${remainingStock.toFixed(2)}, forecast=${forecastVal.toFixed(2)}, partial_coverage=${partialCoverage.toFixed(4)}, coverage_after=${coverage.toFixed(2)}`);
        }
        remainingStock = 0;
        break;
      } else {
        // Stock is negative or zero - continue consuming to allow negative coverage
        remainingStock -= forecastVal;
        coverage += 1.0;
        if (verbose && monthIdx < 20) {
          console.log(`[computeCoverage] Month ${monthIdx}: NEGATIVE STOCK -> stock -= ${forecastVal.toFixed(2)}, coverage += 1.0, stock_after=${remainingStock.toFixed(2)}, coverage_after=${coverage.toFixed(2)}`);
        }
      }
    }
    
    // If stock remains after consuming all forecast months, use historical average for remaining
    // Allow negative coverage: if stock is negative, compute negative coverage
    if (remainingStock !== 0 && historicalAvg > 0) {
      const additionalCoverage = remainingStock / historicalAvg;
      coverage += additionalCoverage;
      if (verbose) {
        console.log(`[computeCoverage] After all forecasts: remaining_stock=${remainingStock.toFixed(2)}, historical_avg=${historicalAvg.toFixed(2)}, additional_coverage=${additionalCoverage.toFixed(4)}, FINAL coverage=${coverage.toFixed(2)}`);
      }
    } else if (verbose) {
      console.log(`[computeCoverage] After all forecasts: remaining_stock=${remainingStock.toFixed(2)}, historical_avg=${historicalAvg.toFixed(2)}, FINAL coverage=${coverage.toFixed(2)}`);
    }
    
    console.debug(
      `[coverage] SKU=${skuId ?? '?'} result: coverage=${coverage.toFixed(2)} remaining_stock=${remainingStock.toFixed(2)}`
    );
    if (verbose) {
      console.log(`[computeCoverage] FINAL RESULT: initialStock=${initialStock.toFixed(2)}, coverage=${coverage.toFixed(2)}, remainingStock=${remainingStock.toFixed(2)}`);
    }
    
    return coverage;
  }, [normalizeForecastValues]);

  const effectiveProjectedInventory = useMemo(() => {
    if (!data) return [] as any[];
    const baseOnhand: Record<string, number> = {};
    const onhandPresence = new Set<string>();
    for (const r of (data.onhand_today || []) as any[]) {
      const sid = String(r.sku_id);
      baseOnhand[sid] = Number(r.onhand_qty ?? 0);
      onhandPresence.add(sid);
    }
    const fm = data.forecast_matrix || [];
    const fcols = fm.length > 0 ? Object.keys(fm[0]).filter((c) => c.startsWith('f_')) : [];
    const H = Math.max(12, fcols.length);
    // Build combined inbound: backend inbound_by_month is 1-based; draft overlay uses 0-based L -> shift by +1
    const combinedInbound: Record<string, Record<number, number>> = {};
    for (const r of ((data.inbound_by_month || []) as any[])) {
      const sku = String((r as any).sku_id);
      const m1 = Number((r as any).arrival_month);
      const q = Number((r as any).inbound_qty || 0);
      if (!(q > 0) || !(m1 >= 1)) continue;
      (combinedInbound[sku] ||= {});
      combinedInbound[sku][m1] = (combinedInbound[sku][m1] || 0) + q;
    }
    // Overlay draft inbound
    for (const [sku, byM] of Object.entries(draftInboundByMonth)) {
      for (const [m0Str, qty] of Object.entries(byM || {})) {
        const m0 = Number(m0Str);
        const q = Number(qty || 0);
        if (!(q > 0)) continue;
        const m1 = m0 + 1; // shift to 1-based
        (combinedInbound[sku] ||= {});
        combinedInbound[sku][m1] = (combinedInbound[sku][m1] || 0) + q;
      }
    }
    
    // Pre-compute historical averages and last prediction indices for all SKUs
    const historicalAvgMap: Record<string, number> = {};
    const avgMap: Record<string, number> = {};
    const lastPredictionIndexMap: Record<string, number | null> = {};
    const asOfDateStr = data.as_of_date || new Date().toISOString().slice(0, 10);
    
    for (const r of (data.avg_forecast || []) as any[]) {
      avgMap[String(r.sku_id)] = Number(r.avg_forecast || 0);
    }
    for (const r of (fm as any[])) {
      const sku = String(r.sku_id);
      const historicalAvg = computeHistoricalAverage(sku, 12);
      historicalAvgMap[sku] = historicalAvg > 0 ? historicalAvg : (avgMap[sku] || 0);
      lastPredictionIndexMap[sku] = getLastPredictionMonthIndex(sku, asOfDateStr);
    }
    
    const rows: any[] = [];
    for (const r of (fm as any[])) {
      const sku = String(r.sku_id);
      let inv = Number(baseOnhand[sku] || 0);
      if (debugSkuSet.has(sku)) {
        // Log initial state
        console.groupCollapsed(`[Proj] SKU=${sku} start_onhand=${inv}`);
      }
      if (!allowNegativeStock) inv = Math.max(0, inv);
      
      // Extract forecast values for this SKU
      const forecastValues = fcols.map((col, idx) => Number(r[col] || 0));
      
      // Get historical average and last prediction index
      const historicalAvg = historicalAvgMap[sku] || 0;
      const avgF = avgMap[sku] || historicalAvg;
      const lastIdx = lastPredictionIndexMap[sku] ?? null;
      
      // Compute coverage for month 0 using new formula
      // Verbose logging disabled by default (set to true to enable detailed coverage logs)
      const verboseLogging = false; // Set to true or check specific SKU condition if needed for debugging
      const coverage0 = computeCoverageFromForecastConsumption(inv, forecastValues, historicalAvg, lastIdx, avgF, sku, verboseLogging);
      rows.push({ 
        sku_id: sku, 
        month: 0, 
        projected_onhand: inv, 
        projected_coverage_months: !allowNegativeStock ? Math.max(0, coverage0) : coverage0, 
        has_onhand: onhandPresence.has(sku) 
      });
      
      for (let m = 0; m < H; m++) {
        const inboundNext = Number(combinedInbound[sku]?.[m + 1] || 0);
        inv += inboundNext;
        inv -= Number(r[`f_${m}`] || 0);
        if (!allowNegativeStock) inv = Math.max(0, inv);
        
        // Compute coverage using new formula: consume remaining forecasts from this month onwards
        const remainingForecasts = forecastValues.slice(m + 1);
        // Adjust last_prediction_index for remaining forecasts (subtract m + 1)
        const remainingLastIdx = lastIdx !== null ? (lastIdx >= (m + 1) ? lastIdx - (m + 1) : null) : null;
        // Verbose logging disabled by default (set to true to enable detailed coverage logs)
        const verboseLogging = false; // Set to true or check specific SKU condition if needed for debugging
        const coverage = computeCoverageFromForecastConsumption(inv, remainingForecasts, historicalAvg, remainingLastIdx, avgF, sku, verboseLogging);
        
        rows.push({ 
          sku_id: sku, 
          month: m + 1, 
          projected_onhand: inv, 
          projected_coverage_months: !allowNegativeStock ? Math.max(0, coverage) : coverage, 
          has_onhand: onhandPresence.has(sku) 
        });
        if (debugSkuSet.has(sku) && m < 12) {
          console.debug(`[Proj] m=${m} inbound_next=${inboundNext} forecast=${Number(r[`f_${m}`] || 0)} inv=${inv} coverage=${coverage}`);
        }
      }
      if (debugSkuSet.has(sku)) {
        console.groupEnd();
      }
    }
    return rows;
  }, [data, draftInboundByMonth, allowNegativeStock, debugSkuSet, computeHistoricalAverage, computeCoverageFromForecastConsumption, getLastPredictionMonthIndex]);

  // Effective reorder months from effective projection and safety stock
  // Generic ROM calculator by threshold
  const computeROMByThreshold = useCallback((thresholdMap: Record<string, number>) => {
    if (!data) return [] as any[];
    const ssMap: Record<string, number> = {};
    for (const r of (data.safety_stock || []) as any[]) ssMap[String(r.sku_id)] = Number(r.safety_stock_units || 0);
    const bySku: Record<string, any[]> = {};
    for (const r of effectiveProjectedInventory) (bySku[r.sku_id] ||= []).push(r);
    const out: any[] = [];
    for (const [sku, arr] of Object.entries(bySku)) {
      arr.sort((a, b) => a.month - b.month);
      const threshold = Number(thresholdMap[sku] || 0);
      // When a draft order is already placed, ignore breaches occurring before its earliest arrival
      let searchStart = 0;
      if (applyDraftToProjections) {
        const inboundForSku = draftInboundByMonth[sku] || {};
        const arrivalMonths = Object.keys(inboundForSku).map(Number).filter((m) => inboundForSku[m] > 0);
        if (arrivalMonths.length > 0) {
          // Rows store end-of-month inventory at label `month = m+1`.
          // An arrival at month A affects the row with month = A+1.
          // Therefore start searching at (earliestArrival + 1).
          searchStart = Math.min(...arrivalMonths) + 1;
        }
      }
      const breach = (arr as any[]).find((x) => Number(x.month) >= searchStart && Number(x.projected_onhand) < threshold);
      if (!breach) continue;
      const t = Number((breach as any).month);
      const L = Math.max(0, Number(effectiveLeadTimeBySku[sku] || 0));
      // Latest month to place an order so that it arrives by tb is tb - L
      const reorderBy = Math.max(0, t - L);
      if (debugSkuSet.has(String(sku))) {
        console.groupCollapsed(`[ROM] SKU=${sku} threshold=${threshold} L=${L} searchStart=${searchStart}`);
        try {
          const inboundForSku = draftInboundByMonth[sku] || {};
          console.debug('Inbound (month->qty):', inboundForSku);
          console.debug('Timeline (first 14 months from searchStart):', (arr as any[])
            .filter((x) => x.month >= searchStart)
            .slice(0, 14)
            .map((x) => ({ month: x.month, inv: x.projected_onhand })));
          console.debug(`breach_month=${t} reorder_by=${reorderBy}`);
        } catch {}
        console.groupEnd();
      }
      out.push({ sku_id: sku, reorder_by_month: reorderBy });
    }
    return out;
  }, [data, effectiveProjectedInventory, applyDraftToProjections, draftInboundByMonth, effectiveLeadTimeBySku, debugSkuSet]);

  // SS-mode ROM uses safety stock as threshold
  const effectiveReorderMonthsSS = useMemo(() => {
    if (!data) return [];
    const ssMap: Record<string, number> = {};
    for (const r of (data.safety_stock || []) as any[]) ssMap[String(r.sku_id)] = Number(r.safety_stock_units || 0);
    return computeROMByThreshold(ssMap);
  }, [data, computeROMByThreshold]);

  // CB-mode ROM uses ROP_cb_units as threshold
  const effectiveReorderMonthsCB = useMemo(() => {
    if (!data) return [];
    const cbMap: Record<string, number> = {};
    for (const r of (data.planning_metrics || []) as any[]) {
      cbMap[String(r.sku_id)] = Number((r as any).rop_cb_units || 0);
    }
    return computeROMByThreshold(cbMap);
  }, [data, computeROMByThreshold]);

  const buildAggregateRequest = useCallback((): AggregateRequest | null => {
    // Skip aggregate until ArticleSelector has loaded articles; avoids redundant empty calls on init
    if (!selectedArticleIds?.length) return null;
    const req: AggregateRequest = {
      article_ids: selectedArticleIds,
      as_of_date: new Date().toISOString().slice(0, 10),
      horizon_months: 12,
      // Pass service level to backend (as service_z), treated as probability or z server-side
      service_z: serviceLevel,
      // Pass CB coverage months globally
      coverage_months: coverageMonths,
    };
    // Include supplier/location filters only when toggle is ON
    if (applyLeftFilter) {
      req.supplier_ids = (supplierSelections || []).map((s) => s.supplier_id);
      req.location_groups = (locationSelections || []).map((l) => l.location_group);
    }
    if (isAggregateSnapshotModeEnabled()) {
      req.snapshot = true;
      req.response_mode = 'snapshot';
    }
    return req;
  }, [selectedArticleIds, serviceLevel, coverageMonths, applyLeftFilter, supplierSelections, locationSelections]);

  const aggregateRequestKey = useMemo(() => {
    if (!activeDatabase?.id) return null;
    const req = buildAggregateRequest();
    if (!req) return null;
    return JSON.stringify({ dbId: String(activeDatabase.id), planningMode, req });
  }, [activeDatabase?.id, buildAggregateRequest, planningMode]);

  const refresh = useCallback(async (signal?: AbortSignal) => {
    if (!activeDatabase?.id) return;
    const req = buildAggregateRequest();
    if (!req) return;
    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;
    setLoading(true);
    setError(null);
    try {
      const resp = await fetchInventoryAggregate(String(activeDatabase.id), req, signal);
      // Enrich with article details if needed
      try {
        if (req.article_ids.length > 0) {
          const details = await fetchArticlesByIds(String(activeDatabase.id), req.article_ids, signal);
          (resp as any).article_details = details;
        }
      } catch {}
      if (requestId !== latestRequestIdRef.current || signal?.aborted) return;
      setSectionStates({});
      setData(resp);
    } catch (e: any) {
      if (requestId !== latestRequestIdRef.current || signal?.aborted || e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') return;
      const msg = e?.response?.data?.detail || e?.message || 'Erreur de chargement des données (vérifiez que les fournisseurs et article-fournisseurs sont importés).';
      setError(msg);
      setData(null);
    } finally {
      if (requestId === latestRequestIdRef.current && !signal?.aborted) {
        setLoading(false);
      }
    }
  }, [activeDatabase?.id, buildAggregateRequest]);

  React.useEffect(() => {
    if (!aggregateRequestKey) return;
    activeAggregateAbortRef.current?.abort();
    const controller = new AbortController();
    activeAggregateAbortRef.current = controller;
    refresh(controller.signal);
    return () => {
      controller.abort();
      if (activeAggregateAbortRef.current === controller) {
        activeAggregateAbortRef.current = null;
      }
    };
  }, [aggregateRequestKey, refresh]);

  const ensureSections = useCallback(async (sectionNames: AggregateSectionRequestName[]) => {
    const snapshotId = data?.snapshot?.id;
    if (!activeDatabase?.id || !snapshotId || sectionNames.length === 0) return;

    await Promise.all(sectionNames.map(async (sectionName) => {
      const state = sectionStates[sectionName];
      if (state?.loading || (state?.loaded && state.snapshotId === snapshotId)) return;

      setSectionStates((prev) => ({
        ...prev,
        [sectionName]: { loading: true, loaded: false, error: null, snapshotId },
      }));

      try {
        const sectionData = await fetchInventoryAggregateSection(
          String(activeDatabase.id),
          snapshotId,
          sectionName
        );
        setData((prev) => {
          if (!prev || prev.snapshot?.id !== snapshotId) return prev;
          if (sectionData && typeof sectionData === 'object' && !Array.isArray(sectionData)) {
            return { ...prev, ...(sectionData as Partial<AggregateResponse>) } as AggregateResponse;
          }
          return { ...prev, [sectionName]: sectionData } as AggregateResponse;
        });
        setSectionStates((prev) => ({
          ...prev,
          [sectionName]: { loading: false, loaded: true, error: null, snapshotId },
        }));
      } catch (e: any) {
        setSectionStates((prev) => ({
          ...prev,
          [sectionName]: {
            loading: false,
            loaded: false,
            error: e?.response?.data?.detail || e?.message || 'Erreur de chargement de section',
            snapshotId,
          },
        }));
      }
    }));
  }, [activeDatabase?.id, data?.snapshot?.id, sectionStates]);

  React.useEffect(() => {
    const snapshotId = data?.snapshot?.id;
    if (!snapshotId) return;

    const prefetch = () => {
      ensureSections(BACKGROUND_PREFETCH_SECTIONS);
    };

    const requestIdleCallback = (window as any).requestIdleCallback;
    if (typeof requestIdleCallback === 'function') {
      const idleId = requestIdleCallback(prefetch, { timeout: 2000 });
      return () => {
        const cancelIdleCallback = (window as any).cancelIdleCallback;
        if (typeof cancelIdleCallback === 'function') cancelIdleCallback(idleId);
      };
    }

    const timeoutId = window.setTimeout(prefetch, 500);
    return () => window.clearTimeout(timeoutId);
  }, [data?.snapshot?.id, ensureSections]);

  const computeSuggestedQty = useCallback((skuId: string, targetMonths: number = 2): number | undefined => {
    try {
      if (!data) return undefined;
      const avg = data.avg_forecast.find((r: any) => String(r.sku_id) === String(skuId))?.avg_forecast ?? 0;
      const onhand = data.onhand_today.find((r: any) => String(r.sku_id) === String(skuId))?.onhand_qty ?? 0;
      const rawNeed = Math.max(0, targetMonths * Number(avg) - Number(onhand));
      // Round by MOQ if available
      const moq = data.articles.find((a: any) => String(a.article_id) === String(skuId))?.default_moq ?? 0;
      if (moq && moq > 0) {
        const rounded = Math.max(moq, Math.ceil(rawNeed / moq) * moq);
        return Math.ceil(rounded);
      }
      return Math.ceil(rawNeed);
    } catch {
      return undefined;
    }
  }, [data]);

  const value = useMemo<InventoryContextState>(() => ({
    data,
    loading,
    error,
    sectionStates,
    ensureSections,
    selectedArticleIds,
    setSelectedArticleIds,
    setArticlesFromSelector: (ids: string[]) => {
      setBaseSelectedArticleIds(ids);
      if (!applyLeftFilter) setSelectedArticleIds(ids);
    },
    refresh,
    basket,
    addToBasket: (line: BasketLine) => setBasket((prev) => {
      const exists = prev.find((l) => l.key === line.key);
      if (exists) return prev.map((l) => (l.key === line.key ? { ...l, ...line } : l));
      // fill suggested if missing
      const suggested = line.suggestedQty ?? computeSuggestedQty(line.sku_id, line.targetMonths ?? 2);
      return [...prev, { ...line, suggestedQty: suggested }];
    }),
    removeFromBasket: (key: string) => setBasket((prev) => prev.filter((l) => l.key !== key)),
    clearBasket: () => setBasket([]),
    updateBasketLine: (key: string, partial: Partial<BasketLine>) =>
      setBasket((prev) => prev.map((l) => (l.key === key ? { ...l, ...partial } : l))),
    supplierSelections,
    locationSelections,
    addSupplierSelection: (supplier_id: number, supplier_name?: string) =>
      setSupplierSelections((prev) =>
        prev.find((s) => s.supplier_id === supplier_id) ? prev : [...prev, { supplier_id, supplier_name }]
      ),
    removeSupplierSelection: (supplier_id: number) =>
      setSupplierSelections((prev) => prev.filter((s) => s.supplier_id !== supplier_id)),
    addLocationSelection: (location_group: string) =>
      setLocationSelections((prev) => (prev.find((l) => l.location_group === location_group) ? prev : [...prev, { location_group }])),
    removeLocationSelection: (location_group: string) =>
      setLocationSelections((prev) => prev.filter((l) => l.location_group !== location_group)),
    clearSelections: () => {
      setSupplierSelections([]);
      setLocationSelections([]);
    },
    draft,
    setDraft,
    applyDraftToProjections,
    setApplyDraftToProjections,
    applyLeftFilter,
    setApplyLeftFilter,
    allowNegativeStock,
    setAllowNegativeStock,
    serviceLevel,
    setServiceLevel,
    coverageMonths,
    setCoverageMonths,
    effectiveProjectedInventory,
    effectiveReorderMonthsSS,
    effectiveReorderMonthsCB,
    planningMode,
    setPlanningMode,
  }), [data, loading, error, sectionStates, ensureSections, selectedArticleIds, refresh, basket, supplierSelections, locationSelections, draft, applyDraftToProjections, applyLeftFilter, allowNegativeStock, serviceLevel, coverageMonths, effectiveProjectedInventory, effectiveReorderMonthsSS, effectiveReorderMonthsCB, planningMode, computeSuggestedQty]);

  return (
    <InventoryPlanningContext.Provider value={value}>
      {children}
    </InventoryPlanningContext.Provider>
  );
};

export function useInventoryPlanning() {
  const ctx = useContext(InventoryPlanningContext);
  if (!ctx) throw new Error('useInventoryPlanning must be used within InventoryPlanningProvider');
  return ctx;
}


